/* Formes servies par `GET ged/aliquots` et les gestes associés (03/10/2026).
   Le moteur calcule TOUT (dates limites, premier périmé, ruptures) ; l'écran
   affiche. Une feuille de types, sans import d'exécution. */

export type StatutAliquot = 'congele' | 'decongele' | 'utilise' | 'jete';

export interface AliquotVue {
  documentId: string; code: string; codeLisible: string;
  produit: string; cleProduit: string; niveau: string; numeroLot: string;
  numero: number | null; nombreDuFlacon: number | null;
  flaconRef: string; flaconId: string; numeroFlacon: string; emplacement: string;
  statut: StatutAliquot; dateReconstitution: string; peremptionLot: string;
  /** Les auteurs : la CLÉ est ce qui est stocké, le NOM ce qui s'affiche
   *  (fiche RH, ou libellé du compte à défaut — résolu par le moteur). */
  congeleLe: string; congeleParNom: string; decongeleLe: string; decongelePar: string; decongeleParNom: string;
  destockageMode: '' | 'scan' | 'manuel'; destockageDerogation: boolean; destockageMotif: string;
  utiliseLe: string; utiliseParNom: string; jeteLe: string; jeteParNom: string; jeteMotif: string;
  /** L'aliquot que l'outil désigne pour son produit et son niveau (premier
   *  périmé, premier sorti) — servi par la recherche du déstockage. */
  aPrendreDabord: boolean;
  limiteCongele: string; limiteDecongele: string; manques: string[];
  borne: 'lot' | 'stabilite_congele' | 'stabilite_decongele' | 'aucune';
  /** La borne qui a joué (RQ, 03/10/2026 : la première date échue fait foi, et
   *  on dit laquelle) — `causeLibelle` est la phrase à afficher. */
  cause: CauseEcheance | ''; causeLibelle: string;
  /** Propriétés de notice DE SON FLACON. */
  stabiliteCongeleJours: number | null; stabiliteDecongeleHeures: number | null;
  usageAliquot: 'unique' | 'multiple' | null;
  dansStabilite: true | false | 'inconnu';
  limiteImprimee: string; etiquetteAJour: boolean; impressions: number;
  utilisations: { passageId: string; carteId: string; niveau: number; date: string; lotVerifie: boolean }[];
}

export type CauseEcheance = 'lot' | 'stabilite';

/** Ce qui reste au PARAMÉTRAGE pour un produit de contrôle (03/10/2026) : des
 *  choix du laboratoire, pas des propriétés du produit. Les stabilités, l'usage
 *  d'un aliquot et les volumes se saisissent avec le FLACON. */
export interface ReglageProduitControle {
  libelle: string;
  rythme: 'journalier' | 'lundi-jeudi' | 'hebdomadaire' | 'quinzaine' | 'mensuel' | null;
  niveaux: string[];
  alerteRuptureJours: number | null;
}

/** Le flacon qui pré-remplit une reconstitution (choisi par le moteur,
 *  `modeleDeFlacon`) : le dernier du même libellé et de la même référence
 *  fournisseur qui porte des valeurs de notice. */
export interface ModeleFlacon {
  ref: string; dateReconstitution: string; refFournisseur: string; cleRef: string;
  valeurs: {
    volumeReconstitutionUl: number | null; stabiliteCongeleJours: number | null;
    stabiliteDecongeleHeures: number | null; usageAliquot: 'unique' | 'multiple' | null;
    nombreUnites: string; volumeParUnite: string;
    /* Les trois températures (06/10/2026). Les deux premières viennent de la
       NOTICE, la troisième est un FAIT — où les aliquots du flacon précédent
       sont réellement partis. Elles se reprennent SÉPARÉMENT : voir
       backend/src/materiaux-conservation.ts. */
    temperatureAvant: string; temperatureApres: string; temperatureAliquots: string;
  };
}

/** UN LOT D'ALIQUOTS (06/10/2026) — ce que l'écran montre désormais à la place
 *  d'une liste de tubes numérotés : produit, niveau, date de reconstitution,
 *  nombre restant. Les tubes gardent une ligne EN BASE pour porter l'heure de
 *  sortie et le rattachement au CQI, mais plus aucune identité à l'écran.
 *  Miroir de `LotVu` (backend/src/cqi-aliquots.ts). */
export interface LotVue {
  codeLot: string;
  flaconRef: string;
  produit: string;
  cleProduit: string;
  niveau: string;
  numeroLot: string;
  dateReconstitution: string;
  restants: number;
  sortis: number;
  jetes: number;
  crees: number;
  limite: string;
  cause: CauseEcheance | '';
}

export interface GrillePlancheConf {
  lignes: number; colonnes: number; margeHautMm: number; margeGaucheMm: number;
  largeurMm: number; hauteurMm: number; pasHorizontalMm: number; pasVerticalMm: number;
  /** Jour où une planche imprimée a été lue à la douchette (`AAAA-MM-JJ`), ou vide. */
  verifieLe?: string;
}

export interface ConfigEtiquettesConf {
  largeurMm: number; hauteurMm: number; dpi: 203 | 300; adresse: string; planche: GrillePlancheConf;
  /** Réglages PROPRES AU POSTE, mesurés sur le rouleau et la machine — pas des
   *  constantes du logiciel. Miroir de `ConfigEtiquettes`
   *  (backend/src/etiquettes-zpl.ts). */
  libellesParEtiquette: number; gouttiereMm: number; calageMm: number; moduleDm: number;
}

export interface Rupture {
  restants: number; jourRupture: string; passagesCouverts: number; perdus: number;
  perdusParCause: { lot: number; stabilite: number };
  niveauAlerte: 'rupture' | 'alerte' | 'ok' | 'inconnu'; message: string;
}

export interface ProduitVue {
  cle: string; libelle: string; reglage: ReglageProduitControle | null; manques: string[];
  derniersFlacons: Record<string, Record<string, string | number | boolean | null>>;
  modeles: ModeleFlacon[];
  niveaux: {
    niveau: string; congeles: number; decongeles: number;
    premier: { code: string; limite: string; cause: CauseEcheance | ''; causeLibelle: string } | null;
    rupture: Rupture | null;
  }[];
}

export interface EtatAliquots {
  maintenant: string; aujourdhui: string;
  aliquots: AliquotVue[]; produits: ProduitVue[];
  /** Les LOTS — la vue de stock depuis le 06/10/2026. `aliquots` reste servi
   *  pour les tubes DEHORS (déstockage en cours) et pour les gestes qui
   *  portent sur des tubes, jamais pour présenter le stock. */
  lots: LotVue[];
  aJeter: { code: string; raison: string; cause: CauseEcheance | '' }[];
  etiquettes: ConfigEtiquettesConf & { aConfirmer: string[]; zebraAdmise: { ok: boolean; raison?: string } };
  /** Les disciplines déjà portées par le registre des flacons, et les rythmes
   *  du réglage de passage — pour « Nouveau produit… ». */
  disciplines: string[];
  rythmes: { valeur: string; libelle: string }[];
  peutDeclarer: boolean; peutReprendre: boolean;
}

/** `GET ged/aliquots/recherche` : les LOTS trouvés, dans l'ordre de prise, et
 *  — quand la saisie est le code d'un tube qui n'est plus au congélateur —
 *  pourquoi il n'y figure pas.
 *
 *  ⚠️ DES LOTS, PLUS DES TUBES (RQ, 11/10/2026) : « je vois la liste des tubes
 *  un par un, je veux juste voir l'aliquot en une ligne avec le nombre
 *  restants ». Lister douze tubes identiques fait choisir là où il n'y a rien
 *  à choisir. */
export interface ReponseRecherche {
  resultats: (LotVue & { aPrendreDabord: boolean })[];
  horsCongelateur: { code: string; codeLisible: string; raison: string } | null;
}

/** `GET ged/aliquots/produits-proches` : le produit qu'on veut créer porte-t-il
 *  la clé d'un produit existant, ou une clé très proche ? */
export interface ProximiteProduit {
  cle: string;
  identique: { cle: string; libelle: string } | null;
  proches: { cle: string; libelle: string; raison: string }[];
}

export interface PlusAncien { code: string; numeroFlacon: string; limite: string; emplacement: string }

export type ReponseDestockage =
  | { issue: 'inconnu'; raison: string }
  | { issue: 'refus'; raison: string; aliquot: AliquotVue }
  | { issue: 'plus_ancien'; raison: string; plusAncien: PlusAncien; aliquot: AliquotVue }
  | { issue: 'ok'; derogation: boolean; plusAncien: PlusAncien | null; avertissements: string[]; aliquot: AliquotVue };

/** Ressemble-t-il à un CODE (scan ou frappe complète) plutôt qu'à une
 *  recherche ? Huit chiffres, séparateurs et rangée AZERTY tolérés — la
 *  lecture qui fait foi est celle du moteur (`lireScan`), celle-ci ne fait que
 *  choisir entre « déstocker » et « chercher ». */
export function ressembleACode(saisie: string): boolean {
  const azerty: Record<string, string> = { '&': '1', 'é': '2', '"': '3', "'": '4', '(': '5', '-': '6', 'è': '7', '_': '8', 'ç': '9', 'à': '0' };
  const brut = saisie.trim();
  const a = Array.from(brut).map((c) => azerty[c] ?? c).join('').replace(/[\s.]/g, '');
  const b = brut.replace(/[\s.-]/g, '');
  return /^\d{8}$/.test(a) || /^\d{8}$/.test(b);
}
